import { FC } from 'react'
import useUser from '@/hooks/useUser'
import { useRouter } from 'next/router'
import { FullWidthLoader } from '@saasalerts/ui'
import {
  ALL_THEMES,
  getThemeClass,
  isLoginPage,
  isPublicRoute,
} from '@/utils/constants'
/**
 * Ensures that the page you are trying to access is not accessible if you are logged out
 */
export const AuthenticationGuard: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const router = useRouter()
  const { isLoading, isLoggedOut, data } = useUser()

  const isCurrentPageLoginPage = isLoginPage(router.pathname)
  const theme = getThemeClass(data?.color)

  if (isPublicRoute(router.pathname)) {
    return (
      <div className={ALL_THEMES}>
        <div className={theme}>{children}</div>
      </div>
    )
  }

  if (isLoading) {
    return <FullWidthLoader />
  }

  if (isLoggedOut && !isCurrentPageLoginPage) {
    const currentUrl = router.asPath
    router.push(`/auth?redirect=${encodeURIComponent(currentUrl)}`)
    return null
  }

  if (isLoggedOut && isCurrentPageLoginPage) {
    return (
      /**
       * A little hack to make tailwind believe that all the themes are in use and then its overridden by the actual theme in uses
       */
      <div className={ALL_THEMES}>
        <div className={theme}>{children}</div>
      </div>
    )
  }

  if (!isLoggedOut && isCurrentPageLoginPage) {
    if (router.query.redirect) {
      /**
       * This code is to prevent open redirect attacks
       */
      let shouldRedirect = false

      try {
        const redirect = decodeURIComponent(router.query.redirect as string)

        // Check if the URL starts with "http" and is a valid URL
        if (redirect.startsWith('http')) {
          const url = new URL(redirect)
          // Allow only HTTP(S) URLs that match the current origin
          if (url.origin === window.location.origin) {
            shouldRedirect = true
          } else {
            console.log('Invalid redirect URL')
          }
        } else {
          // Handle relative URLs safely
          const url = new URL(redirect, window.location.origin)
          if (url.origin === window.location.origin) {
            shouldRedirect = true
          } else {
            console.log('Invalid redirect URL')
          }
        }
      } catch (error) {
        console.log('Invalid redirect URL')
      }
      if (!shouldRedirect) {
        router.push('/')
      } else {
        router.push(decodeURIComponent(router.query.redirect as string))
      }
    } else {
      router.push('/')
    }
    return null
  }

  if (!isLoggedOut && !isCurrentPageLoginPage) {
    return (
      /**
       * A little hack to make tailwind believe that all the themes are in use and then its overridden by the actual theme in uses
       */
      <div className={ALL_THEMES}>
        <div className={theme}>
          {children}
          <div id="react-select-portal" />
        </div>
      </div>
    )
  }

  return <div>Please configure Auth Guard</div>
}
